export const GRADES_TO_POINTS = {
  "A+": 13,
  A: 12,
  "A-": 11,
  "B+": 10,
  B: 9,
  "B-": 8,
  "C+": 7,
  C: 6,
  "C-": 5,
  "D+": 4,
  D: 3,
  "D-": 2,
  F: 1,
};

export const TDF_GRADES_TO_POINTS = {
  "A+": 1,
  A: 2,
  "A-": 3,
  "B+": 4,
  B: 5,
  "B-": 6,
  "C+": 7,
  C: 8,
  "C-": 9,
  "D+": 10,
  D: 11,
  "D-": 12,
  F: 13,
};

export const POINTS_TO_GRADES = {
  1: "F",
  2: "D-",
  3: "D",
  4: "D+",
  5: "C-",
  6: "C",
  7: "C+",
  8: "B-",
  9: "B",
  10: "B+",
  11: "A-",
  12: "A",
  13: "A+",
};

export const calculateSimilarity = (tech) => {
  if (
    !tech ||
    !tech.technological_similarity_and_evolution_grade ||
    !tech.application_and_role_alignment_grade ||
    !tech.ecosystem_and_compatibility_grade
  )
    return null;

  const ss =
    GRADES_TO_POINTS[tech.technological_similarity_and_evolution_grade] +
    GRADES_TO_POINTS[tech.application_and_role_alignment_grade] +
    GRADES_TO_POINTS[tech.ecosystem_and_compatibility_grade];

  const MAX_SS = 39; // 3 dimensions × 13 points (A+)
  return {
    score: ss,
    grade: POINTS_TO_GRADES[Math.floor(ss / 3)] || "F",
    percentage: (ss / MAX_SS) * 100,
  };
};

export const calculateSubstitutability = (tech, clr) => {
  const similarity = calculateSimilarity(tech);
  if (!similarity) return null;

  const TDF =
    TDF_GRADES_TO_POINTS[tech.transition_difficulty_factor_grade] || 1;
  const CLR = GRADES_TO_POINTS[clr] || 1;

  // Calculate Adjusted Difficulty Score
  const ADS = Math.max(1, TDF - CLR / 2);

  // Calculate Difficulty Adjustment Factor
  const DAF = 1 - (ADS - 1) / 12;

  const substitutabilityPercentage = similarity.percentage * DAF;

  let grade;

  if (substitutabilityPercentage >= 90) {
    grade = "A+";
  } else if (substitutabilityPercentage >= 80) {
    grade = "A";
  } else if (substitutabilityPercentage >= 70) {
    grade = "B";
  } else if (substitutabilityPercentage >= 60) {
    grade = "C";
  } else if (substitutabilityPercentage >= 50) {
    grade = "D";
  } else if (substitutabilityPercentage >= 0) {
    grade = "F";
  } else {
    grade = "NA";
  }

  return {
    percentage: substitutabilityPercentage,
    grade,
  };
};

export const TECHMAP_FRAMEWORK = `
Comprehensive Technology Transition Assessment Framework

Introduction

This framework is designed to assess the potential success of a candidate transitioning from one technology (Technology A) to another (Technology B). It evaluates:

	1.	Similarity between Technologies: How similar are Technology A and Technology B across key dimensions?
	2.	Transition Difficulty Factor (TDF): How inherently difficult is the transition from Technology A to Technology B?
	3.	Candidate Learning Rate (CLR): How capable is the candidate in learning new technologies?

By systematically assessing these components and combining them through a structured calculation, the framework provides a Substitutability Score that indicates the likelihood of a successful transition.

Framework Components

1. Similarity Assessment

The similarity assessment measures how closely related Technology A is to Technology B across three key dimensions:

Dimension 1: Technological Similarity and Evolution

	•	Definition: Evaluates the inherent technological characteristics, paradigms, and evolution paths of the two technologies.
	•	Considerations:
	•	Language Type: Are both technologies programming languages, frameworks, or tools?
	•	Paradigms: Do they share programming paradigms (e.g., object-oriented, functional)?
	•	Evolution: Has one technology evolved from the other, or do they share a common ancestor?
	•	Syntax and Structure: How similar are the syntax and structural elements?
	•	Assigning a Grade:
Grade Criteria:
	•	A+: Technologies are almost identical or one is a superset of the other. Minimal differences in syntax and structure.
	•	A: Technologies share significant similarities in paradigms, syntax, and evolution. Differences are minor and easily manageable.
	•	A-: Technologies are closely related with some differences in syntax or features but share the same core principles.
	•	B+: Technologies share common paradigms and have several similarities but have noticeable differences in syntax or features.
	•	B: Technologies have some similarities in paradigms or concepts but differ in syntax and features. Transition requires learning new elements.
	•	B-: Technologies have limited similarities; they may be in the same category but differ significantly in implementation and usage.
	•	C+: Technologies are in related fields but have substantial differences in paradigms, syntax, and features.
	•	C: Technologies are in the same broad domain but differ greatly in technology stack, paradigms, and usage.
	•	C-: Technologies share minimal similarities; they may be used for similar purposes but are technologically distinct.
	•	D+: Technologies are largely different with very few overlapping concepts or features.
	•	D: Technologies have almost no technological similarities; they belong to different categories or serve different purposes.
	•	D-: Technologies are entirely different with no shared paradigms or features.
	•	F: Technologies are completely unrelated technologically; they serve different functions and have no overlapping concepts.

Dimension 2: Application and Role Alignment

	•	Definition: Assesses how similar the roles and applications associated with each technology are.
	•	Considerations:
	•	Job Functions: Do the roles associated with both technologies involve similar responsibilities?
	•	Industry Domain: Are both technologies used within the same industry or sector?
	•	End-User Impact: Do they contribute to similar end products or services?
	•	Assigning a Grade:
Grade Criteria:
	•	A+: Roles are identical or highly overlapping; the candidate’s existing experience aligns perfectly with the new role.
	•	A: Roles are very similar with minor differences in responsibilities or focus areas.
	•	A-: Roles share significant similarities but may involve different methodologies or tools.
	•	B+: Roles have considerable overlap but require adaptation to different workflows or processes.
	•	B: Roles are related but involve some different responsibilities or focus areas.
	•	B-: Roles are somewhat related but have notable differences in day-to-day activities.
	•	C+: Roles are in the same general field but differ significantly in function and responsibilities.
	•	C: Roles are in related industries but involve different types of work and objectives.
	•	C-: Roles have minimal overlap; the candidate’s experience only partially applies to the new role.
	•	D+: Roles are largely different with very limited applicability of previous experience.
	•	D: Roles are substantially different with almost no overlap in responsibilities or required skills.
	•	D-: Roles are entirely different; the candidate’s previous experience does not apply to the new role.
	•	F: Roles are completely unrelated; they are in different fields with no shared responsibilities or objectives.

Dimension 3: Ecosystem and Compatibility

	•	Definition: Evaluates the compatibility and interoperability between the technologies, including tools, libraries, and platforms.
	•	Considerations:
	•	Tools and IDEs: Do both technologies use the same or similar development tools and environments?
	•	Libraries and Frameworks: Is there compatibility or interoperability between libraries and frameworks used?
	•	Platforms: Do the technologies operate on the same platforms or systems?
	•	Community and Support: Is there a shared community or resources that support both technologies?
	•	Assigning a Grade:
Grade Criteria:
	•	A+: Ecosystems are identical or fully compatible; tools and libraries are interchangeable.
	•	A: High level of compatibility; minor adjustments needed to use existing tools and libraries.
	•	A-: Significant compatibility with some differences in tools or configurations required.
	•	B+: Considerable compatibility; many tools and libraries can be used with modifications.
	•	B: Moderate compatibility; some tools and practices are transferable, but new tools need to be learned.
	•	B-: Limited compatibility; few tools or libraries are shared, requiring adaptation to new ones.
	•	C+: Minimal compatibility; most tools and libraries are different, but underlying concepts may be similar.
	•	C: Ecosystems are largely different; tools and libraries are not compatible, requiring learning new systems.
	•	C-: Ecosystems are significantly different with minimal overlap in tools or platforms.
	•	D+: Ecosystems are almost entirely different; little to no compatibility exists.
	•	D: Ecosystems are entirely different with no shared tools, libraries, or platforms.
	•	D-: Ecosystems are completely incompatible; technologies operate in different environments with no overlap.
	•	F: No compatibility whatsoever; technologies belong to entirely separate domains with distinct ecosystems.

2. Transition Difficulty Factor (TDF)

The TDF assesses the inherent difficulty of transitioning from Technology A to Technology B, independent of the candidate’s abilities.

	•	Considerations:
	•	Complexity of Technology B: How complex is Technology B compared to Technology A?
	•	Learning Curve: Does Technology B have a steep learning curve due to new paradigms or concepts?
	•	Prerequisite Knowledge: Are there prerequisites that the candidate may lack?
	•	Resource Availability: Are there sufficient learning resources available to facilitate the transition?
	•	Assigning a Grade:
Grade Criteria:
	•	A+: Transition is very easy; minimal effort required to learn Technology B.
	•	A: Transition is straightforward with minor learning required.
	•	A-: Transition is relatively easy; candidate can adapt quickly with some study.
	•	B+: Transition requires moderate effort; candidate needs to learn new concepts but they are not overly complex.
	•	B: Transition involves learning several new concepts and may require structured learning.
	•	B-: Transition is somewhat challenging; Technology B has significant differences requiring dedicated learning.
	•	C+: Transition is challenging; candidate must learn many new concepts and possibly unlearn old habits.
	•	C: Transition is difficult; Technology B is complex and differs greatly from Technology A.
	•	C-: Transition is very difficult; steep learning curve with many advanced concepts.
	•	D+: Transition is extremely difficult; candidate must acquire extensive new knowledge and skills.
	•	D: Transition is exceptionally difficult; Technology B is highly complex and may require formal education.
	•	D-: Transition is nearly impossible without significant time investment and training.
	•	F: Transition is impractical; differences are so vast that it would be like starting a new career.

3. Candidate Learning Rate (CLR)

The CLR evaluates the candidate’s ability to learn and adapt to new technologies.

	•	Considerations:
	•	Past Experience: Has the candidate successfully learned new technologies in the past?
	•	Adaptability: How quickly does the candidate adapt to changes and new environments?
	•	Motivation: Is the candidate motivated and enthusiastic about learning Technology B?
	•	Learning Resources: Does the candidate have access to resources that suit their learning style?
	•	Assigning a Grade:
Grade Criteria:
	•	A+: Candidate is exceptionally quick at learning new technologies; has a proven track record of rapid adaptation.
	•	A: Candidate learns new technologies quickly and efficiently; highly adaptable.
	•	A-: Candidate is a fast learner; adapts well to new technologies with minimal guidance.
	•	B+: Candidate learns new technologies at an above-average pace; shows initiative in self-learning.
	•	B: Candidate has an average learning rate; can learn new technologies with standard training and practice.
	•	B-: Candidate may require additional time or support to learn new technologies but is capable.
	•	C+: Candidate learns at a slower pace; may need structured training programs.
	•	C: Candidate struggles with learning new technologies; may require significant support and time.
	•	C-: Candidate has difficulty adapting to new technologies; learning new skills is challenging.
	•	D+: Candidate is resistant to change; learning new technologies is a significant hurdle.
	•	D: Candidate has a very slow learning rate; may not be able to adapt to new technologies effectively.
	•	D-: Candidate shows minimal ability or willingness to learn new technologies; significant barriers exist.
	•	F: Candidate is unable or unwilling to learn new technologies; transition is not feasible.

Calculation Steps

1. Calculate Similarity Score (SS)

Sum the grade points assigned for each of the three similarity dimensions.

	•	Grade Points:
	•	A+ = 13 points
	•	A  = 12 points
	•	A- = 11 points
	•	B+ = 10 points
	•	B  = 9 points
	•	B- = 8 points
	•	C+ = 7 points
	•	C  = 6 points
	•	C- = 5 points
	•	D+ = 4 points
	•	D  = 3 points
	•	D- = 2 points
	•	F  = 1 point

Formula:

SS = Grade Points for Dimension 1 + Grade Points for Dimension 2 + Grade Points for Dimension 3

	•	Maximum Similarity Score (MaxSS):
MaxSS = 3 dimensions × 13 points (A+) = 39 points

2. Calculate Similarity Percentage (SP)

Convert the Similarity Score to a percentage of the maximum possible score.

Formula:

SP = (SS / MaxSS) × 100

3. Calculate Adjusted Difficulty Score (ADS)

Subtract half of the Candidate Learning Rate (CLR) grade points from the Transition Difficulty Factor (TDF) grade points, ensuring the result is at least 1.

	•	Grade Points for TDF and CLR:
	•	Same as above.

Formula:

ADS = max(1, TDF - (CLR / 2))

4. Calculate Difficulty Adjustment Factor (DAF)

Scale the Adjusted Difficulty Score to a factor between 0 and 1.

Formula:

DAF = 1 - ((ADS - 1) / 12)

	•	Explanation: Since TDF grades range from 1 (A+) to 13 (F), and ADS cannot be less than 1, subtracting 1 and dividing by 12 normalizes the value to a 0 to 1 scale.

5. Calculate Substitutability Score (Sub₍A→B₎)

Multiply the Similarity Percentage by the Difficulty Adjustment Factor.

Formula:

Sub₍A→B₎ = SP × DAF

	•	Result: A percentage between 0% and 100% indicating the overall substitutability.

Interpreting the Substitutability Score

	•	80% - 100% (Grade A): High Substitutability
	•	Candidate is highly suitable for the transition.
	•	Minimal training or adjustment needed.
	•	70% - 79% (Grade B): Good Substitutability
	•	Candidate is suitable with some training.
	•	Transition is likely to be successful.
	•	60% - 69% (Grade C): Moderate Substitutability
	•	Candidate may require significant training.
	•	Transition is possible but will need support.
	•	50% - 59% (Grade D): Low Substitutability
	•	Candidate requires extensive training.
	•	Transition may be challenging.
	•	Below 50% (Grade F): Very Low Substitutability
	•	Candidate may not be suitable without major investment.
	•	Transition is unlikely to be successful without significant effort.

Applying the Framework: A Detailed Example

Transition from PHP Developer to Python Developer

Scenario: Can a PHP developer transition to a Python developer role?

1. Similarity Assessment

Dimension 1: Technological Similarity and Evolution

	•	Assessment:
	•	Both are high-level, interpreted scripting languages used primarily for web development.
	•	Similarities:
	•	Dynamic typing.
	•	Support for object-oriented programming.
	•	Differences:
	•	Syntax and language constructs.
	•	Python emphasizes readability and simplicity; PHP has more complex syntax.
	•	Grade Assignment:
	•	Grade: B+ (10 points)
	•	Justification: Technologies share common paradigms and purposes but have noticeable differences in syntax and features.

Dimension 2: Application and Role Alignment

	•	Assessment:
	•	Roles involve backend web development, working with databases, and handling server-side logic.
	•	Grade Assignment:
	•	Grade: A- (11 points)
	•	Justification: Roles are closely related with some differences in frameworks and tools used.

Dimension 3: Ecosystem and Compatibility

	•	Assessment:
	•	Differences:
	•	Different frameworks (Laravel for PHP, Django/Flask for Python).
	•	Package managers differ (Composer vs. pip).
	•	Similarities:
	•	Both can interact with databases like MySQL.
	•	Web server configuration knowledge is transferable.
	•	Grade Assignment:
	•	Grade: B (9 points)
	•	Justification: Moderate compatibility; some tools and practices are transferable, but new tools need to be learned.

Total Similarity Score (SS):

SS = 10 (B+) + 11 (A-) + 9 (B) = 30 points

2. Calculate Similarity Percentage (SP)

SP = (30 / 39) × 100 ≈ 76.92%

3. Transition Difficulty Factor (TDF)

	•	Assessment:
	•	Learning Python syntax and idioms.
	•	Understanding Python-specific frameworks.
	•	Grade Assignment:
	•	Grade: B (5 points)
	•	Justification: Transition involves learning several new concepts and may require structured learning.

4. Candidate Learning Rate (CLR)

	•	Assessment:
	•	Candidate has previously learned multiple programming languages.
	•	Demonstrates adaptability and self-learning skills.
	•	Grade Assignment:
	•	Grade: B+ (10 points)
	•	Justification: Candidate learns new technologies at an above-average pace and shows initiative.

5. Calculate Adjusted Difficulty Score (ADS)

ADS = max(1, 5 (TDF) - (10 (CLR) / 2)) = max(1, 5 - 5) = 1

6. Calculate Difficulty Adjustment Factor (DAF)

DAF = 1 - ((1 - 1) / 12) = 1 - 0 = 1

7. Calculate Substitutability Score (Sub₍A→B₎)

Sub₍PHP→Python₎ = 76.92% × 1 = 76.92%

8. Interpretation

	•	Substitutability Score: 76.92%
	•	Grade: B (Good Substitutability)
	•	Recommendation:
	•	Candidate is suitable for the transition with some training.
	•	Existing programming experience and learning ability support a successful transition.
	•	Focus on learning Python syntax and familiarizing with popular frameworks.

Final Notes

	•	Customization: Adjust the framework to better fit specific organizational needs or industry contexts.
	•	Holistic Evaluation: Consider other factors such as soft skills, team dynamics, and cultural fit.
	•	Continuous Improvement: Use feedback from actual transitions to refine the assessment process.

By following this detailed framework, organizations can make informed decisions about a candidate’s potential to transition between technologies, thereby optimizing talent utilization and development.
`.trim();
